import React, { useEffect, useRef } from "react";
import Card from "@mui/material/Card";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import { useAttachmentIdFromRoute, useGameIdFromRoute } from "../games/WithGameIdFromRoute";
import { useGame, useGameSellsheets, useUpdateSellSheet } from "@seabrookstudios/pitch2table";
import { ImageOrPdfViewer } from "../core/ImageOrPdfViewer";
import CardHeader from "@mui/material/CardHeader";
import Download from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import { assetPath } from "../endpoints";
import Link from "@mui/material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import Delete from "@mui/icons-material/Delete";
import RestoreFromTrash from "@mui/icons-material/RestoreFromTrash";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CardActions from "@mui/material/CardActions";
import { EditableCardHeader } from "../games/EditableHeading";
import { CircularProgressSize } from "../theme";
import Stack from "@mui/material/Stack";
import { SellsheetFeedbackDialog } from "./SellsheetFeedbackDialog";

export const SellsheetFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const attachmentId = useAttachmentIdFromRoute();
  const updateSellsheet = useUpdateSellSheet(gameId, attachmentId);
  const { data: sellSheets = [] } = useGameSellsheets(gameId);
  const { data: game } = useGame(gameId);

  /** Keeps last known row while GameSellsheets refetches so detail (and open modals) do not unmount. */
  /** @type {React.MutableRefObject<import("@seabrookstudios/pitch2table-core").SellSheet | undefined>} */
  const sellsheetStableRef = useRef(undefined);

  useEffect(() => {
    sellsheetStableRef.current = undefined;
  }, [attachmentId]);

  if (!attachmentId) {
    return <EmptyDetailCard text="Select a sellsheet from the list on the left." />;
  }

  const sellsheetLive = sellSheets.find((s) => s.id === attachmentId);
  if (sellsheetLive) {
    sellsheetStableRef.current = sellsheetLive;
  }
  const sellsheet = sellsheetLive ?? sellsheetStableRef.current;

  /**
   * @param {string} name
   */
  const updateName = async (name) => {
    await updateSellsheet.mutateAsync({ deleted: undefined, name });
  };

  const recover = async () => {
    await updateSellsheet.mutateAsync({ deleted: false, name: undefined });
  };

  const trash = async () => {
    await updateSellsheet.mutateAsync({ deleted: true, name: undefined });
  };

  if (!sellsheet || !game) {
    return <CircularProgress />;
  }

  const Icon = sellsheet.deleted ? RestoreFromTrash : Delete;

  return (
    <Card elevation={1} square={false}>
      <Stack direction="row" justifyContent="space-between">
        <EditableCardHeader
          title="Name"
          subheader="Name"
          text={sellsheet.name || ""}
          onSave={updateName}
          helperText="A name for this sell sheet version e.g. v3"
        />
        <CardHeader
          action={
            <Stack direction="row" spacing={1}>
              <SellsheetFeedbackDialog gameId={gameId} attachmentId={attachmentId} sellsheet={sellsheet} />
              <IconButton
                component={Link}
                href={assetPath(sellsheet.s3location)}
                download={`${game.name} - Sellsheet.pdf`}
              >
                <Download />
              </IconButton>
            </Stack>
          }
        />
      </Stack>
      {sellsheet.deleted ? (
        <Box p={2}>
          <Alert severity="warning">This file has been deleted. You can restore it using the button below.</Alert>
        </Box>
      ) : (
        <>
          <ImageOrPdfViewer document={sellsheet} />
        </>
      )}
      <CardActions>
        <IconButton onClick={sellsheet.deleted ? recover : trash}>
          {updateSellsheet.isPending ? <CircularProgress size={CircularProgressSize} /> : <Icon />}
        </IconButton>
      </CardActions>
    </Card>
  );
};
